import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import PersonRounded from "@mui/icons-material/PersonRounded";
import ChatRounded from "@mui/icons-material/ChatRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import Header from "../Header";
import Footer from "../Footer";
import { useCookies } from "react-cookie";
import User from "../../../api/User";
import { useNavigate } from "react-router-dom";
import { TAG_COLORS } from "../../../utils/tagColors";
import SearchBar from "../../SearchBar";
import Waitlist from "../Calendar/Add/Waitlist";
import { Dayjs } from "dayjs";
import Booking from "../../../api/Booking";
import { APP_MAX_WIDTH } from "../../../constants/layout";
import TagFilterBar from "../../TagFilterBar";
import EmptyListState from "../../EmptyListState";
import { FONTS } from "../../../constants/styles";

const tagsColors = TAG_COLORS;

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

/* ── Hero ── */

const UsersHero: React.FC<{ count: number }> = ({ count }) => (
    <Box
        sx={{
            bgcolor: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "16px",
            px: 2,
            py: 1.5,
            position: "relative",
            zIndex: 1,
        }}
    >
        <Stack spacing={1}>
            <Typography
                fontFamily={FONTS.display}
                fontWeight={700}
                fontSize="28px"
                lineHeight={1.2}
                color="white"
            >
                Clients
            </Typography>
            {count > 0 && (
                <Stack direction="row" alignItems="center">
                    <Box
                        sx={{
                            bgcolor: "rgba(255,255,255,0.15)",
                            borderRadius: "12px",
                            px: 1.25,
                            py: 0.25,
                        }}
                    >
                        <Typography
                            fontSize="12px"
                            fontWeight={600}
                            color="white"
                        >
                            {count} client{count !== 1 ? "s" : ""}
                        </Typography>
                    </Box>
                </Stack>
            )}
        </Stack>
    </Box>
);

/* ── Main Component ── */

const UsersList = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [tags, setTags] = useState<string[]>([]);
    const [selectedTag, setSelectedTag] = useState<string>("All");
    const [users, setUsers] = useState<User[]>([]);
    const [categorizedUsers, setCategorizedUsers] = useState<
        { letter: string; users: User[] }[]
    >([]);

    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);

    /* ── Single scroll container ── */
    const scrollRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const [scrollY, setScrollY] = useState(0);
    const [showDotHint, setShowDotHint] = useState(
        () => localStorage.getItem("app_dot_hint_dismissed") !== "true"
    );

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setScrollY(el.scrollTop);
    }, []);

    // On filter/search change, scroll just enough to hide the hero
    // so toolbar is sticky and first result is visible
    useLayoutEffect(() => {
        const el = scrollRef.current;
        const toolbar = toolbarRef.current;
        if (!el || !toolbar) return;
        // toolbar.offsetTop = distance from scroll container top to toolbar
        // Subtract the sticky top offset (header height) so toolbar sits flush
        const headerHeight = 56; // matches Header component
        const target = toolbar.offsetTop - headerHeight;
        if (el.scrollTop < target) return; // already above, don't jump down
        el.scrollTop = target;
    }, [selectedTag, searchTerm]);

    useEffect(() => {
        if (!uuid || !brand_id) {
            navigate("/");
            return;
        }
        setLoading(true);
        User.listEx(uuid, brand_id)
            .then((res) => setUsers(res ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [uuid, brand_id, navigate]);

    useEffect(() => {
        const uniqueTags = Array.from(
            new Set(users.map((user) => user.status).filter(Boolean))
        );

        const sortedTags = uniqueTags.sort(
            (a, b) =>
                tagsColors.findIndex((tagObj) => tagObj.tag === a) -
                tagsColors.findIndex((tagObj) => tagObj.tag === b)
        );

        const orderedTags = ["All", "Waitlist", ...sortedTags];
        setTags(orderedTags);
    }, [users]);

    useEffect(() => {
        let categorizedUsers: { letter: string; users: User[] }[] = [];

        for (const user of users.filter((x): boolean => {
            const matchesTag =
                selectedTag === "All" || x.status === selectedTag;
            const matchesSearch = x.name
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            return matchesTag && matchesSearch;
        })) {
            const names = user.name.split(" ");
            let letter: string;

            if (names.length > 1 && names[names.length - 1].length > 0) {
                letter = names[names.length - 1][0].toUpperCase();
            } else {
                letter = names[0][0].toUpperCase();
            }

            const index = categorizedUsers.findIndex(
                (x) => x.letter.toUpperCase() === letter
            );
            if (index >= 0) {
                categorizedUsers[index].users.push(user);
            } else {
                categorizedUsers.push({ letter, users: [user] });
            }
        }

        setCategorizedUsers(
            categorizedUsers.sort((a, b) => a.letter.localeCompare(b.letter))
        );
    }, [users, selectedTag, searchTerm]);

    const handleTagChange = (tag: string) => {
        if (tag === selectedTag) return;
        setSelectedTag(tag);
    };

    // Calculate horizontal position of the FAB
    const fabRight =
        typeof window !== "undefined" && window.visualViewport
            ? window.visualViewport.width > APP_MAX_WIDTH
                ? `${APP_MAX_WIDTH / 10}px`
                : "9vw"
            : "9vw";

    // Global stagger index across all groups
    let globalIndex = 0;

    return (
        <>
            <Header scrollY={scrollY} />

            {/* ── Single scroll (Pattern A) ── */}
            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden" }}
                bgcolor="white"
            >
                {/* Hero */}
                <Stack
                    width="100%"
                    sx={{
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        pt: "calc(72px + env(safe-area-inset-top))",
                        pb: "40px",
                        px: 2,
                        boxSizing: "border-box",
                        position: "relative",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    <UsersHero count={users.length} />
                </Stack>

                {/* ── Bottom sheet (toolbar sticks, list flows) ── */}
                <Stack
                    sx={{
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        bgcolor: "white",
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    {/* Sticky toolbar */}
                    <Box
                        ref={toolbarRef}
                        sx={{
                            position: "sticky",
                            top: "calc(56px + env(safe-area-inset-top))",
                            zIndex: 4,
                            bgcolor: "white",
                            borderTopLeftRadius: "24px",
                            borderTopRightRadius: "24px",
                        }}
                    >
                        <Box sx={{ pt: 2, pb: 0.5 }}>
                            <TagFilterBar
                                tags={tags}
                                selectedTag={selectedTag}
                                onSelectTag={handleTagChange}
                            />
                        </Box>
                        <Box sx={{ px: 2, pb: 1 }}>
                            <SearchBar
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                            />
                        </Box>
                    </Box>

                    {/* List content — scrolls naturally */}
                    {selectedTag === "Waitlist" ? (
                        <Waitlist
                            accountKey={uuid}
                            brandKey={brand_id}
                            onSelect={(entry) => {
                                return async (
                                    dateTime: Dayjs,
                                    close: () => void,
                                    setLoading: (
                                        val: boolean
                                    ) => void
                                ) => {
                                    const endDate = dateTime
                                        .add(
                                            entry.serviceDuration || 0,
                                            "minute"
                                        )
                                        .toDate();

                                    await Booking.update(
                                        uuid,
                                        brand_id,
                                        location_id,
                                        entry.bookingKey,
                                        dateTime.toDate(),
                                        endDate
                                    );

                                    close();
                                };
                            }}
                            mode="datetime"
                        />
                    ) : !loading && categorizedUsers.length === 0 ? (
                        <EmptyListState
                            primaryText={
                                users.length === 0
                                    ? "No clients yet"
                                    : "No clients found"
                            }
                            secondaryText={
                                users.length === 0
                                    ? "Add your first client to get started"
                                    : undefined
                            }
                            button={
                                users.length === 0
                                    ? {
                                          text: "Add Client",
                                          icon: (
                                              <AddRounded
                                                  sx={{
                                                      fontSize: 18,
                                                      color: primary,
                                                  }}
                                              />
                                          ),
                                          onClick: () =>
                                              navigate(
                                                  "/users/registration"
                                              ),
                                      }
                                    : undefined
                            }
                        />
                    ) : (
                        <Stack
                            spacing={3}
                            sx={{
                                px: 2,
                                pb: "200px",
                                "@keyframes fadeSlideIn": {
                                    from: {
                                        opacity: 0,
                                        transform: "translateY(12px)",
                                    },
                                    to: {
                                        opacity: 1,
                                        transform: "translateY(0)",
                                    },
                                },
                                animation: "fadeSlideIn 0.3s ease-out",
                            }}
                        >
                            {/* One-time hint explaining the green dot */}
                            {showDotHint && users.some((u) => u.appInstalled) && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{
                                        bgcolor: alpha("#4CAF50", 0.08),
                                        borderRadius: "10px",
                                        px: 1.5,
                                        py: 1,
                                    }}
                                >
                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#4CAF50", flex: "0 0 auto" }} />
                                    <Typography fontSize="12px" color="text.secondary" flex={1}>
                                        Green dot means the client has your app installed
                                    </Typography>
                                    <ButtonBase
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowDotHint(false);
                                            localStorage.setItem("app_dot_hint_dismissed", "true");
                                        }}
                                        sx={{ p: 0.5, borderRadius: "50%", flex: "0 0 auto" }}
                                    >
                                        <CloseRounded sx={{ fontSize: 14, color: "text.secondary" }} />
                                    </ButtonBase>
                                </Stack>
                            )}

                            {categorizedUsers.map((category) => {
                                const groupStartIndex = globalIndex;
                                globalIndex += category.users.length;

                                return (
                                    <Stack
                                        key={category.letter}
                                        spacing={0.5}
                                    >
                                        {/* Alphabet header */}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{ px: 0.5, mb: 1 }}
                                        >
                                            <Typography
                                                className="no-translation"
                                                fontFamily={FONTS.display}
                                                fontWeight={700}
                                                fontSize="14px"
                                                color={primary}
                                                sx={{ flexShrink: 0 }}
                                            >
                                                {category.letter}
                                            </Typography>
                                            <Box
                                                flex={1}
                                                height="1px"
                                                bgcolor={alpha(
                                                    primary,
                                                    0.1
                                                )}
                                            />
                                        </Stack>

                                        {/* User rows */}
                                        {category.users.map((user, i) => (
                                            <React.Fragment key={user.key}>
                                                <UserBlock
                                                    user={user}
                                                    index={
                                                        groupStartIndex + i
                                                    }
                                                />
                                                {i <
                                                    category.users.length -
                                                        1 && (
                                                    <Box
                                                        sx={{
                                                            height: "1px",
                                                            bgcolor:
                                                                "rgba(0,0,0,0.06)",
                                                            ml: "60px",
                                                        }}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </Stack>

            {/* FAB */}
            {categorizedUsers.length > 0 && (
                <Box
                    position="fixed"
                    bottom="130px"
                    left="50%"
                    zIndex={5}
                    sx={{
                        transform: "translateX(-50%)",
                        width: "100%",
                        maxWidth: APP_MAX_WIDTH,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <ButtonBase
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: "16px",
                            bgcolor: primary,
                            marginRight: fabRight,
                            boxShadow: `0 4px 14px ${alpha(primary, 0.35)}`,
                            transition: "transform 0.15s ease",
                            "&:active": { transform: "scale(0.92)" },
                            "@keyframes fadeSlideUp": {
                                from: {
                                    opacity: 0,
                                    transform: "translateY(16px)",
                                },
                                to: { opacity: 1, transform: "translateY(0)" },
                            },
                            animation: "fadeSlideUp 0.4s ease-out 0.3s both",
                        }}
                        onClick={() => navigate("/users/registration")}
                    >
                        <AddRounded sx={{ color: "white", fontSize: 24 }} />
                    </ButtonBase>
                </Box>
            )}

            <Footer tab="users" />
        </>
    );
};

/* ── User Row ── */

const UserBlock = (props: { user: User; index: number }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const primary = theme.palette.primary.main;

    const { user, index } = props;

    const formatName = (x: string) => {
        const names = x.split(" ");
        if (names.length > 1) {
            return `${names[0]} ${names.slice(1).join(" ")}`;
        }
        return names[0];
    };

    const statusColor =
        tagsColors.find((x) => x.tag === user.status)?.color ?? primary;

    return (
        <ButtonBase
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                py: 1,
                px: 0.5,
                borderRadius: "12px",
                "@keyframes fadeSlideUp": {
                    from: { opacity: 0, transform: "translateY(10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `fadeSlideUp 0.25s ease-out ${index * 30}ms both`,
            }}
            onClick={() => navigate(`/users/info/${user.key}`)}
        >
            {/* Avatar with app-installed dot */}
            <Box sx={{ position: "relative", flex: "0 0 auto" }}>
                {user.image ? (
                    <Box
                        sx={{
                            width: 44,
                            minWidth: 44,
                            height: 44,
                            borderRadius: "14px",
                            backgroundImage: `url(${user.image})`,
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "cover",
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: 44,
                            minWidth: 44,
                            height: 44,
                            borderRadius: "14px",
                            bgcolor: alpha(primary, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <PersonRounded
                            sx={{ fontSize: 22, color: primary, opacity: 0.6 }}
                        />
                    </Box>
                )}
                {user.appInstalled && (
                    <Box sx={{
                        position: "absolute", bottom: -1, right: -1,
                        width: 10, height: 10, borderRadius: "50%",
                        bgcolor: "#4CAF50",
                        border: "2px solid white",
                    }} />
                )}
            </Box>

            {/* Info */}
            <Stack
                direction="column"
                flex={1}
                justifyContent="center"
                alignItems="flex-start"
                spacing={0.5}
                ml={1.5}
                sx={{ overflow: "hidden" }}
            >
                <Typography
                    className="no-translation"
                    fontFamily={FONTS.body}
                    fontWeight={600}
                    fontSize="15px"
                    lineHeight={1.3}
                    noWrap
                >
                    {formatName(user.name)}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    {user.status && (
                        <Box
                            sx={{
                                bgcolor: alpha(statusColor, 0.1),
                                borderRadius: "10px",
                                px: 0.75,
                                py: 0.25,
                            }}
                        >
                            <Typography
                                fontWeight={500}
                                fontSize="11px"
                                lineHeight={1.3}
                                color={statusColor}
                            >
                                {user.status}
                            </Typography>
                        </Box>
                    )}
                    {(user.total ?? 0) > 0 && (
                        <Typography
                            fontSize="12px"
                            color="text.secondary"
                        >
                            €{user.total}
                        </Typography>
                    )}
                </Stack>
            </Stack>

            {/* Action icon box */}
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "10px",
                    bgcolor: alpha(primary, 0.06),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    ml: 1,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                        user.rkey
                            ? `/users/link/${user.key}`
                            : `/chats/${user.key}`
                    );
                }}
            >
                {user.rkey ? (
                    <LinkRounded sx={{ fontSize: 16, color: "text.secondary" }} />
                ) : (
                    <ChatRounded sx={{ fontSize: 16, color: primary }} />
                )}
            </Box>
        </ButtonBase>
    );
};

export default UsersList;
